import { ContentItemViewNodesType } from '../../../types'
import { PromoteDemoteActions } from '../constants'
import { PromoteDemoteAction } from '../types'
import { findSiblings } from './nodeHelpers'
import {
    calculateDemoteVariables,
    calculatePromoteVariables,
} from './orderCalculators'

type PartialNode = Partial<ContentItemViewNodesType>

/**
 * Updates a node's parent references based on the new parent
 * @param node - Node to update (will be mutated)
 * @param newParent - New parent node or undefined for root level
 */
const updateNodeParentReferences = (
    node: PartialNode,
    newParent: PartialNode | undefined
): void => {
    if (newParent) {
        node.dtvParentId = newParent.dtvItemId
        node.dtvContentItemParentId = newParent.id
    } else {
        // moving to root level
        node.dtvParentId = undefined
        node.dtvContentItemParentId = undefined
    }
}


/**
 * Adjusts sibling order values when a node is inserted or removed
 * @param siblings - Sibling nodes to adjust
 * @param threshold - Order value threshold for adjustment
 * @param increment - true: shift right (insertion), false: shift left (removal)
 */
const adjustSiblingOrders = (
    siblings: PartialNode[],
    threshold: number,
    increment: boolean
): void => {
    siblings.forEach((sibling) => {
        if (
            sibling.order !== undefined &&
            (increment ? sibling.order >= threshold : sibling.order > threshold)
        ) {
            sibling.order += increment ? 1 : -1
        }
    })
}

/**
 * Calculates new parent and order position based on action type
 * @param action - 'Promote' or 'Demote'
 * @param currentParent - Current parent of the node being moved
 * @param nodeToMove - Node being promoted/demoted
 * @param nodes - All nodes in the tree
 * @param paragraphLevel - Current paragraph level (used for special cases)
 * @returns Object with newParent and order position
 */
const getNewPositionData = (
    action: PromoteDemoteAction,
    currentParent: PartialNode | undefined,
    nodeToMove: PartialNode,
    nodes: PartialNode[],
    paragraphLevel: number
) => {
    return action === PromoteDemoteActions.Promote
        ? calculatePromoteVariables(currentParent, nodes, paragraphLevel)
        : calculateDemoteVariables(nodeToMove, currentParent, nodes)
}

/**
 * Creates an optimistic version of the content tree after a promote/demote operation
 * @param nodes - Current content item view nodes
 * @param contentId - ID of the content being promoted/demoted
 * @param action - Promote or Demote action
 * @param paragraphLevel - Current paragraph level
 * @returns Optimistically updated nodes
 */
export const createOptimisticTreeState = (
    nodes: PartialNode[],
    contentId: string,
    action: PromoteDemoteAction,
    paragraphLevel: number
): PartialNode[] => {
    // 1. Find the node to move by element ID
    const nodeToMove = nodes.find((node) =>
        node.elements?.some((element) => element.id === contentId)
    )

    if (!nodeToMove || !nodeToMove.id) {
        return nodes // Return unchanged if node not found
    }

    // 2. Find current parent and get new position data
    const currentParent = nodes.find((node) => {
        if (nodeToMove.dtvContentItemParentId) {
            return node.id === nodeToMove.dtvContentItemParentId
        }
        return node.dtvItemId === nodeToMove.dtvParentId
    })

    const { newParent, order: newOrder } = getNewPositionData(
        action,
        currentParent,
        nodeToMove,
        nodes,
        paragraphLevel
    )

    // 3. Clone nodes to avoid mutations
    const updatedNodes = nodes.map((node) => ({ ...node }))

    // Find the moved node in the cloned array
    const movedNode = updatedNodes.find((n) => n.id === nodeToMove.id)!
    const oldOrder = movedNode.order ?? 0

    // 4. Update the moved node's parent references and order
    updateNodeParentReferences(movedNode, newParent)
    movedNode.order = newOrder

    // 5. Handle order adjustments based on action type
    if (action === PromoteDemoteActions.Promote) {
        // PROMOTE: Shift siblings right at target level (≥ newOrder)
        const targetSiblings = findSiblings(
            updatedNodes,
            newParent,
            nodeToMove.id
        )
        adjustSiblingOrders(targetSiblings, newOrder, true) // increment = true

        // PROMOTE: Fill gap at old level (shift remaining children up)
        const oldSiblings = findSiblings(
            updatedNodes,
            currentParent,
            nodeToMove.id
        )
        adjustSiblingOrders(oldSiblings, oldOrder, false) // increment = false
    } else {
        // DEMOTE: Fill gap at old level (shift siblings left after demoted node)
        const oldSiblings = findSiblings(
            updatedNodes,
            currentParent,
            nodeToMove.id
        )
        adjustSiblingOrders(oldSiblings, oldOrder, false) // increment = false

        // DEMOTE: No shifting at target level (append to end)
        // New parent's children order remains unchanged
    }

    return updatedNodes
}
